import type { ProductCardProps } from '@/lib/types';
import imageUrlBuilder from '@sanity/image-url';
import type { ProductPageProps } from '@veranda/shared/components/pages/product-details/client';
import type { ElevarViewProduct } from '@veranda/shared/elevar';
import { removeGid } from '@veranda/shared/lib';
import { client } from '@veranda/shared/sanity/utils/client';

const builder = imageUrlBuilder(client);

export function serializeProductCardToElevarProduct({
  product,
  list,
  position,
}: {
  product: ProductCardProps;
  list: string;
  position: number;
}): ElevarViewProduct & { position: number } {
  return {
    id: product.sku ?? '',
    variant_id: product.variantGid ? removeGid(product.variantGid) : '',
    product_id: product.productGid ? removeGid(product.productGid) : '',
    name: product.title,
    brand: product.brand?.name ?? 'Tilecloud',
    category: product.category ?? '',
    variant: 'Default Title',
    price: product.price ? String(product.price) : '0',
    compare_at_price: product.compareAtPrice
      ? String(product.compareAtPrice)
      : '0',
    image: product.mainImage?.asset
      ? builder.image(product.mainImage.asset).auto('format').fit('max').url()
      : '',
    list,
    position,
  };
}

export function serializeProductPageToElevarProduct({
  product,
  list,
}: {
  product: ProductPageProps;
  list: string;
}): ElevarViewProduct {
  const activeVariant = product.variants?.[0];

  return {
    id: activeVariant?.sku ?? '',
    variant_id: activeVariant?.gid ? removeGid(activeVariant.gid) : '',
    product_id: product.productGid ? removeGid(product.productGid) : '',
    name: product.title ?? '',
    brand: product.brand?.name ?? 'Tilecloud',
    category: product.category ?? '',
    variant: activeVariant?.title ?? '',
    price: activeVariant?.price ? String(activeVariant.price) : '0',
    compare_at_price: activeVariant?.compareAtPrice
      ? String(activeVariant.compareAtPrice)
      : '0',
    image: product.mainImage?.asset
      ? builder.image(product.mainImage.asset).auto('format').fit('max').url()
      : '',
    list,
  };
}

const portableTextSharedStyles = [
  // Paragraph styles
  '[&_p]:mb-4',
  '[&_p]:text-primary/90',
  '[&_p]:font-normal',
  '[&_p]:typography-outfit-body',

  //   List styles
  '[&_ul]:mt-4',
  '[&_ul]:mb-8',
  '[&_ul]:pl-4',
  '[&_ul]:space-y-3',
  '[&_ul]:list-inside',
  '[&_ul]:list-disc',
  '[&_ul]:typography-outfit-body',
  '[&_ul]:font-normal',

  // Link styles
  '[&_a]:my-4',

  // Global styles
  'font-medium',
  '[&_*:not(#portable-text-button):not(#portable-text-button_*)]:text-primary',

  // Image styles
  '[&_img]:my-6',

  // Table styles
  '[&_#custom-table]:mb-6',

  // Youtube embed styles
  '[&_#youtube-embed]:mb-6',
];

export const portableTextBlogStyles = [
  ...portableTextSharedStyles,

  // Typography styles
  '[&_h2]:typography-outfit-h2',
  '[&_h3]:typography-outfit-h3',
  '[&_h4]:typography-outfit-h4',
  '[&_h5]:typography-outfit-h5',
  '[&_h6]:typography-outfit-h6',

  // Heading margins
  '[&_h2]:mt-12',
  '[&_h2]:mb-6',

  '[&_h3]:mt-10',
  '[&_h3]:mb-5',

  '[&_h4]:mt-8',
  '[&_h4]:mb-4',

  '[&_h5]:mt-6',
  '[&_h5]:mb-3',

  '[&_h6]:mt-4',
  '[&_h6]:mb-2',
].join(' ');

export const portableTextModuleStyles = [
  ...portableTextSharedStyles,

  // Typography styles (one size down from blog)
  '[&_h2]:typography-outfit-h3',
  '[&_h3]:typography-outfit-h4',
  '[&_h4]:typography-outfit-h5',
  '[&_h5]:typography-outfit-h6',
  '[&_h6]:typography-outfit-h6',

  '[&_p[data-size="sm"]]:text-sm',

  // Heading margins
  '[&_h2]:mt-10',
  '[&_h2]:mb-5',

  '[&_h3]:mt-8',
  '[&_h3]:mb-4',

  '[&_h4]:mt-6',
  '[&_h4]:mb-3',

  '[&_h5]:mt-4',
  '[&_h5]:mb-2',

  '[&_h6]:mt-4',
  '[&_h6]:mb-2',
].join(' ');
